import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApplication } from '../../services/rbacApi';
import { Application } from '../../types/rbac.types';
import PermissionCategoriesManager from '../../components/rbac/PermissionCategoriesManager';
import { Button } from '../../components/ui/UIComponents';

const ApplicationSettingsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<Application | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchApplication(id);
    }
  }, [id]);

  const fetchApplication = async (applicationId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getApplication(applicationId);
      setApplication(data);
    } catch (err: any) {
      console.error('Error fetching application:', err);
      setError('Failed to load application details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    if (id) {
      fetchApplication(id);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="p-6">
        <div className="bg-red-900/30 border border-red-500 text-red-300 p-4 rounded-lg">
          {error || 'Application not found'}
        </div>
        <div className="mt-4">
          <Button onClick={() => navigate('/rbac/applications')} variant="outline">
            Back to Applications
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Application Settings: {application.name}
          </h1>
          <p className="text-gray-400 mt-1">
            Manage application-specific settings and configurations
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleRefresh} variant="outline">
            Refresh
          </Button>
          <Button onClick={() => navigate(`/access-control`)} variant="outline">
            Back to Applications
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Permission Categories</h2>
          <p className="text-gray-400 mb-4">
            Manage the categories available for permissions in this application. These categories help organize permissions in the UI.
          </p>
          
          <PermissionCategoriesManager 
            applicationId={id!} 
            onUpdate={handleRefresh}
          />
        </section>
        
        {/* Additional application settings sections can be added here */}
      </div>
    </div>
  );
};

export default ApplicationSettingsPage;
